/**
 * Bottle form component for create/edit
 */

import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Button, Input, Select, Combobox, Checkbox, ImageUpload } from '../common';
import { DistillerySelect, InlineDistilleryForm } from '../distilleries';
import { useStorePicks } from '../../hooks/useBottles';
import { BOTTLE_TYPES, BOTTLE_STATUSES, BOTTLE_SIZES } from '../../utils/constants';
import { formatDateForInput } from '../../utils/formatters';
import './BottleForm.css';

/**
 * Form for creating or editing a bottle
 * @param {Object} props - Component props
 * @param {Object} props.bottle - Existing bottle data for editing or cloning
 * @param {Function} props.onSubmit - Submit handler
 * @param {Function} props.onCancel - Cancel handler
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.isClone - Whether the form is for cloning a bottle
 * @param {boolean} props.shareImages - When cloning, reuse the source bottle's images instead of requiring new uploads (only valid when cloning your own bottle)
 * @returns {React.ReactElement} Form element
 */
export default function BottleForm({
  bottle,
  onSubmit,
  onCancel,
  loading = false,
  isClone = false,
  shareImages = false,
  submitLabel
}) {
  const { data: storePicks = [] } = useStorePicks();
  const [showNewDistillery, setShowNewDistillery] = useState(false);
  const [showAgeCalc, setShowAgeCalc] = useState(false);
  const [calcYears, setCalcYears] = useState('');
  const [calcMonths, setCalcMonths] = useState('');

  const [pendingImages, setPendingImages] = useState({
    front: null,
    back: null
  });
  const [removedImages, setRemovedImages] = useState({
    front: false,
    back: false
  });

  const getDefaultValues = () => {
    if (bottle && isClone) {
      return {
        ...bottle,
        purchase_date: '',
        purchase_price: '',
        date_emptied: '',
        status: 'Unopened',
        distillery_id: bottle.distillery_id || '',
        image_front_url: shareImages ? (bottle.image_front_url || null) : null,
        image_back_url: shareImages ? (bottle.image_back_url || null) : null
      };
    }
    if (bottle) {
      return {
        ...bottle,
        purchase_date: formatDateForInput(bottle.purchase_date),
        date_emptied: formatDateForInput(bottle.date_emptied),
        distillery_id: bottle.distillery_id || ''
      };
    }
    return {
      bottle_name: '',
      type: '',
      status: 'Unopened',
      distillery_id: '',
      purchase_date: '',
      age_months: '',
      proof: '',
      purchase_price: '',
      size: '750ml',
      single_barrel: false,
      bottled_in_bond: false,
      cask_strength: false,
      small_batch: false,
      double_barreled: false,
      full_proof: false,
      store_pick: '',
      finish_cask: '',
      notes: '',
      tasting_notes: ''
    };
  };

  const isEdit = bottle && !isClone;

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: getDefaultValues()
  });

  const onFormSubmit = (data) => {
    // Remove related data and metadata fields that shouldn't be submitted
    const { distilleries, id, user_id, created_at, updated_at, created_by, updated_by, image_front_url, image_back_url, ...formData } = data;
    // When cloning with shareImages, point the new bottle at the source bottle's
    // existing image URLs (unless the user removed or replaced them in this form)
    // instead of uploading duplicate files.
    const sharedImageUrls = isClone && shareImages ? {
      image_front_url: removedImages.front || pendingImages.front ? null : (image_front_url || null),
      image_back_url: removedImages.back || pendingImages.back ? null : (image_back_url || null)
    } : {};
    const processedData = {
      ...formData,
      ...sharedImageUrls,
      distillery_id: formData.distillery_id || null,
      age_months: formData.age_months ? parseInt(formData.age_months, 10) : null,
      proof: formData.proof ? parseFloat(formData.proof) : null,
      purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
      purchase_date: formData.purchase_date || null,
      date_emptied: formData.status === 'Empty'
        ? (bottle?.date_emptied || new Date().toISOString().split('T')[0])
        : null,
      pendingImages,
      removedImages,
      existingImages: {
        front: image_front_url || null,
        back: image_back_url || null
      }
    };
    onSubmit(processedData);
  };

  const handleImageChange = (side) => (file) => {
    if (file) {
      setPendingImages(prev => ({ ...prev, [side]: file }));
      setRemovedImages(prev => ({ ...prev, [side]: false }));
    } else {
      setPendingImages(prev => ({ ...prev, [side]: null }));
      // If there was an existing image, mark it as removed
      const hasExistingImage = (isEdit || (isClone && shareImages)) && bottle &&
        (side === 'front' ? bottle.image_front_url : bottle.image_back_url);
      if (hasExistingImage) {
        setRemovedImages(prev => ({ ...prev, [side]: true }));
      }
    }
  };

  const handleAgeCalcApply = () => {
    const y = parseInt(calcYears, 10) || 0;
    const m = parseInt(calcMonths, 10) || 0;
    const total = y * 12 + m;
    if (total > 0) {
      setValue('age_months', total);
    }
    setShowAgeCalc(false);
    setCalcYears('');
    setCalcMonths('');
  };

  const handleDistilleryCreated = (newDistillery) => {
    setValue('distillery_id', newDistillery.id);
    setShowNewDistillery(false);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="bottle-form">
      <div className="bottle-form__section">
        <h3 className="bottle-form__section-title">Basic Information</h3>
        <div className="bottle-form__grid">
          <Input
            label="Bottle Name"
            required
            error={errors.bottle_name?.message}
            {...register('bottle_name', { required: 'Bottle name is required' })}
          />

          <Select
            label="Type"
            required
            options={BOTTLE_TYPES}
            error={errors.type?.message}
            {...register('type', { required: 'Type is required' })}
          />

          <Select
            label="Status"
            options={BOTTLE_STATUSES}
            error={errors.status?.message}
            {...register('status')}
          />

          <Select
            label="Size"
            options={BOTTLE_SIZES}
            error={errors.size?.message}
            {...register('size')}
          />
        </div>
      </div>

      <div className="bottle-form__section">
        <h3 className="bottle-form__section-title">Distillery</h3>
        {showNewDistillery ? (
          <InlineDistilleryForm
            onSave={handleDistilleryCreated}
            onCancel={() => setShowNewDistillery(false)}
          />
        ) : (
          <div className="bottle-form__distillery-row">
            <Controller
              name="distillery_id"
              control={control}
              render={({ field }) => (
                <DistillerySelect
                  {...field}
                  error={errors.distillery_id?.message}
                />
              )}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowNewDistillery(true)}
            >
              + New Distillery
            </Button>
          </div>
        )}
      </div>

      <div className="bottle-form__section">
        <h3 className="bottle-form__section-title">Details</h3>
        <div className="bottle-form__grid">
          <div className="bottle-form__age-wrap">
            <div className="bottle-form__age-row">
              <Input
                label="Age (Months)"
                type="number"
                min="0"
                error={errors.age_months?.message}
                {...register('age_months', {
                  min: { value: 0, message: 'Age must be positive' }
                })}
              />
              <button
                type="button"
                className="bottle-form__age-calc-btn"
                onClick={() => setShowAgeCalc(v => !v)}
                title="Age calculator"
                aria-label="Open age calculator"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="2" width="16" height="20" rx="2" />
                  <rect x="7" y="5" width="10" height="4" rx="1" />
                  <circle cx="8" cy="14" r="0.8" fill="currentColor" stroke="none" />
                  <circle cx="12" cy="14" r="0.8" fill="currentColor" stroke="none" />
                  <circle cx="16" cy="14" r="0.8" fill="currentColor" stroke="none" />
                  <circle cx="8" cy="18" r="0.8" fill="currentColor" stroke="none" />
                  <circle cx="12" cy="18" r="0.8" fill="currentColor" stroke="none" />
                  <circle cx="16" cy="18" r="0.8" fill="currentColor" stroke="none" />
                </svg>
              </button>
            </div>
            {showAgeCalc && (
              <div className="bottle-form__age-popup">
                <p className="bottle-form__age-popup-title">Age Calculator</p>
                <div className="bottle-form__age-popup-fields">
                  <label>
                    <span>Years</span>
                    <input
                      type="number"
                      min="0"
                      max="99"
                      placeholder="0"
                      value={calcYears}
                      onChange={(e) => setCalcYears(e.target.value)}
                      autoFocus
                    />
                  </label>
                  <label>
                    <span>Months</span>
                    <input
                      type="number"
                      min="0"
                      max="11"
                      placeholder="0"
                      value={calcMonths}
                      onChange={(e) => setCalcMonths(e.target.value)}
                    />
                  </label>
                </div>
                <div className="bottle-form__age-popup-actions">
                  <button type="button" className="bottle-form__age-popup-cancel" onClick={() => { setShowAgeCalc(false); setCalcYears(''); setCalcMonths(''); }}>
                    Cancel
                  </button>
                  <button type="button" className="bottle-form__age-popup-ok" onClick={handleAgeCalcApply}>
                    OK
                  </button>
                </div>
              </div>
            )}
          </div>

          <Input
            label="Proof"
            type="number"
            step="0.01"
            min="0"
            max="200"
            error={errors.proof?.message}
            {...register('proof', {
              min: { value: 0, message: 'Proof must be positive' },
              max: { value: 200, message: 'Proof cannot exceed 200' }
            })}
          />

          <Input
            label="Purchase Price"
            type="number"
            step="0.01"
            min="0"
            error={errors.purchase_price?.message}
            {...register('purchase_price', {
              min: { value: 0, message: 'Price must be positive' }
            })}
          />

          <Input
            label="Purchase Date"
            type="date"
            error={errors.purchase_date?.message}
            {...register('purchase_date')}
          />

          <Controller
            name="store_pick"
            control={control}
            render={({ field }) => (
              <Combobox
                label="Store Pick"
                placeholder="e.g., Total Wine, Costco"
                options={storePicks}
                error={errors.store_pick?.message}
                {...field}
              />
            )}
          />

          <Input
            label="Finish Cask"
            placeholder="e.g., Sherry, Port"
            error={errors.finish_cask?.message}
            {...register('finish_cask')}
          />
        </div>
      </div>

      <div className="bottle-form__section">
        <h3 className="bottle-form__section-title">Characteristics</h3>
        <div className="bottle-form__checkboxes">
          <Controller
            name="single_barrel"
            control={control}
            render={({ field: { value, onChange, ...field } }) => (
              <Checkbox
                label="Single Barrel"
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
                {...field}
              />
            )}
          />
          <Controller
            name="bottled_in_bond"
            control={control}
            render={({ field: { value, onChange, ...field } }) => (
              <Checkbox
                label="Bottled-in-Bond"
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
                {...field}
              />
            )}
          />
          <div className="bottle-form__checkbox-with-tip">
            <Controller
              name="cask_strength"
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <Checkbox
                  label="Cask Strength"
                  checked={value}
                  onChange={(e) => onChange(e.target.checked)}
                  {...field}
                />
              )}
            />
            <span
              className="bottle-form__tip-icon"
              data-tip="Bottled directly from the barrel without dilution. The proof varies by barrel and batch."
              aria-label="Cask Strength info"
            >i</span>
          </div>
          <Controller
            name="small_batch"
            control={control}
            render={({ field: { value, onChange, ...field } }) => (
              <Checkbox
                label="Small Batch"
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
                {...field}
              />
            )}
          />
          <Controller
            name="double_barreled"
            control={control}
            render={({ field: { value, onChange, ...field } }) => (
              <Checkbox
                label="Double Barreled"
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
                {...field}
              />
            )}
          />
          <div className="bottle-form__checkbox-with-tip">
            <Controller
              name="full_proof"
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <Checkbox
                  label="Full Proof"
                  checked={value}
                  onChange={(e) => onChange(e.target.checked)}
                  {...field}
                />
              )}
            />
            <span
              className="bottle-form__tip-icon"
              data-tip="Bottled at the same proof the spirit entered the barrel (typically 125 proof for bourbon). Unlike Cask Strength, this is a specific standard — not just whatever proof remains after aging."
              aria-label="Full Proof info"
            >i</span>
          </div>
        </div>
      </div>

      <div className="bottle-form__section">
        <h3 className="bottle-form__section-title">Notes</h3>
        <div className="bottle-form__notes">
          <div className="input-group">
            <label className="input-group__label">Notes</label>
            <textarea
              className="input-group__input"
              rows="3"
              placeholder="General notes about this bottle..."
              {...register('notes')}
            />
          </div>
          <div className="input-group">
            <label className="input-group__label">Tasting Notes</label>
            <textarea
              className="input-group__input"
              rows="3"
              placeholder="Flavor profile, aroma, finish..."
              {...register('tasting_notes')}
            />
          </div>
        </div>
      </div>

      <div className="bottle-form__section">
        <h3 className="bottle-form__section-title">Images</h3>
        <div className="bottle-form__images">
          <ImageUpload
            label="Front Label"
            value={removedImages.front ? null : ((isEdit || (isClone && shareImages)) ? bottle?.image_front_url : null)}
            pendingFile={pendingImages.front}
            onChange={handleImageChange('front')}
            disabled={loading}
          />
          <ImageUpload
            label="Back Label"
            value={removedImages.back ? null : ((isEdit || (isClone && shareImages)) ? bottle?.image_back_url : null)}
            pendingFile={pendingImages.back}
            onChange={handleImageChange('back')}
            disabled={loading}
          />
        </div>
      </div>

      <div className="bottle-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" loading={loading}>
          {submitLabel ?? (isClone ? 'Clone Bottle' : isEdit ? 'Save Changes' : 'Add Bottle')}
        </Button>
      </div>
    </form>
  );
}
